import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";
import "./Table.css"; // Assuming you still want basic table styling
import { saveAs } from 'file-saver';
import {useNavigate} from "react-router-dom";
import {toast} from "react-hot-toast";

const VendorList = () => {
    const [vendorData, setVendorData] = useState([]);
    const [searchFilter, setSearchFilter] = useState("");
    const [userType, setUserType] = useState(localStorage.getItem("panelAccess"))
    const navigate = useNavigate();
    const [csvFile, setCsvFile] = useState(null);


    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.accessor("userName", {
                header: () => "Vendor Name",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("vendorCode", {
                header: () => "Vendor Code",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("companyName", {  // Changed 'unit' to 'companyName'
                header: () => "Company Name", // Changed "Unit" to "Company Name"
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("city", {  // Changed 'zoneName' to 'city'
                header: () => "City", // Changed "Zone Name" to "City"
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("state", {  // Changed 'zoneName' to 'city'
                header: () => "State", // Changed "Zone Name" to "City"
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("emailId", {
                header: () => "Email",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("phoneNum", {
                header: () => "Phone Number",
                cell: (info) => info.getValue(),
            }),
            columnHelper.display({
                id: "menu",
                header: () => "",
                cell: ({ row }) => (
                    <>
                        <button onClick={() => {
                            row.original._id ? navigate(`/vendor/${row.original._id}`) :  toast.error("Vendor not registered yet!")
                        }}
                                className="btn btn-primary"><span className="text-bold text-xl"> ↻ </span>Update Vendor
                        </button>
                        <button onClick={() => navigate(`/tasksList/all/?vendor=${row.original._id}`)}
                                className="ml-22 btn btn-secondary"><span className="text-bold text-xl"> ↸ </span>View Tasks
                        </button>
                    </>
                )
            })
            // Add any other relevant vendor information columns here.
        ],
        [navigate] // Added navigate to dependency array
    );

    const table = useReactTable({
        data: vendorData,
        columns,
        state: {
            globalFilter: searchFilter,
        },
        onGlobalFilterChange: setSearchFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });


    const getVendorList = async () => {
        const userToken = localStorage.getItem("user-token");

        const headers = {
            "authorization": userToken,
        };

        let bUrl = userType === "admin" ? `/channel/vendors` : `/channel/vendorRegistration`; //  Endpoint for vendors

        try {
            const response = await axios.get(bUrl, {
                headers: headers,
                params: { search: searchFilter }, // Pass the search query
            });

            // Map the API response to include city and companyName
            const mappedData = response.data.map(vendor => ({
                ...vendor,
                city: vendor.city || '',  // Use a default value if city is null/undefined
                companyName: vendor.companyName || '', // Use a default value if companyName is null/undefined
            }));

            setVendorData(mappedData); // Assuming the API returns { data: [vendors] }
        } catch (error) {
            console.error("Error fetching vendor data:", error);
            // Optionally handle the error (e.g., display an error message)
        }
    };

    const downloadCSV = () => {
        if (!vendorData || vendorData.length === 0) {
            return;
        }

        const headers = ['vendorCode', 'city', 'companyName', 'emailId', 'phoneNum']; // Define the headers you want to download
        const csvRows = [];

        csvRows.push(headers.join(',')); // Add headers

        for (const row of vendorData) {
            const values = headers.map(header => {
                let value = row[header];
                if (value === null || value === undefined) {
                    value = '';
                }

                // Escape commas and quotes
                let processedValue = String(value).replace(/"/g, '""');
                if (processedValue.includes(',') || processedValue.includes('"')) {
                    processedValue = `"${processedValue}"`;
                }
                return processedValue;
            });
            csvRows.push(values.join(','));
        }

        const csvData = csvRows.join('\n');

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'vendors.csv');
    };

    const handleFileUpload = (event) => {
        setCsvFile(event.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!csvFile) {
            alert("Please select a CSV file");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const csvText = e.target.result;
            const lines = csvText.split('\n');
            const headers = lines[0].split(',');
            console.log(headers)
            const emailIndex = headers.findIndex(header => header.trim().toLowerCase() === 'emailid');
            const vendorCodeIndex = headers.findIndex(header => header.trim().toLowerCase() === 'vendorcode');

            if (emailIndex === -1) {
                alert("CSV file must contain an 'emailId' column.");
                return;
            }
            if (vendorCodeIndex === -1) {
                alert("CSV file must contain an 'VendorCode' column.");
                return;
            }

            const vendors = [];
            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(',');
                if (values[emailIndex]) {
                    vendors.push({
                        email: values[emailIndex].trim(),
                        vendorCode: values[vendorCodeIndex].trim()
                    });
                }
            }

            if (vendors.length === 0) {
                alert("No emails found in the CSV file.");
                return;
            }

            const userToken = localStorage.getItem("user-token");
            const headersConfig = {
                "authorization": userToken,
            };

            try {
                await axios.post(
                    `/channel/vendorRegistration`, // Same URL for both GET and POST
                    { vendors }, // Sending emails in the 'vendors' key
                    { headers: headersConfig }
                );
                alert("Vendors added successfully!");
                getVendorList(); // Refresh vendor list
                setCsvFile(null); // Reset file input
            } catch (error) {
                console.error("Error adding vendors:", error);
                alert("Error adding vendors. Please check the console for details.");
            }
        };
        reader.readAsText(csvFile);
    };


    const downloadSampleCSV = () => {
        const csvData = "emailId,vendorCode\n" +
            "vendor1@example.com,1234A\n" +
            "vendor2@example.com,1234B\n" +
            "vendor3@example.com,1234C";

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'sample_vendors.csv');
    };

    useEffect(() => {
        getVendorList();
    }, [searchFilter]); //  Fetch data when the component mounts and when searchFilter changes

    useEffect(() => {
        setUserType(localStorage.getItem("panelAccess"));
    }, []);

    return (
        <div className={"flex"}>
            <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold mb-4">Vendors</h1>
                    <button onClick={downloadCSV} className="btn btn-primary">
                        <span className="text-bold text-xl"> ↯ </span> Download CSV
                    </button>
                </div>
                <div className="p-4">
                    <input
                        type="text"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        placeholder="Search by Vendor Code, City, Company Name, Email..."
                        className="input input-bordered w-full"
                    />
                </div>

                {userType !== 'admin' && (
                    <div className="p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="flex items-center space-x-4">
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    className="file-input file-input-bordered w-full max-w-xs"
                                />
                                <button type="submit" className="btn btn-success">
                                    Upload CSV
                                </button>
                                <button type="button" onClick={downloadSampleCSV} className="btn btn-info">
                                    Download Sample CSV
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="table w-full">
                        <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.column.getCanSort() ? "cursor-pointer select-none" : ""}`}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        {{
                                            asc: " ↑",
                                            desc: " ↓",
                                        }[header.column.getIsSorted()] ?? null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div className="join p-4">
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {">"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        {">>"}
                    </button>
                    <span style={{alignItems: 'center'}} className="flex input input-bordered join-item">
            Page
                        {' '}
                        <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
            </strong>
          </span>
                    <span className="flex items-center gap-1">

            <input
                type="number"
                min={1}
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                }}
                className="input input-bordered w-24"
            />
          </span>
                    <select
                        className="select select-bordered"
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default VendorList;
